import { observable, action, computed, runInAction, makeObservable } from 'mobx'
import api from '../../api'
import { dataStore } from '../UIStore'
import { DEFAULT_COLOUR, fieldError } from '../../components/tools/toolbar/ManageModalControls.jsx'

// Both tabs share one UI flow but call separate status resources.
const statusApi = {
  shape: {
    list: api.getShapeStatuses,
    create: api.createShapeStatus,
    update: api.updateShapeStatus,
    remove: api.deleteShapeStatus,
    reorder: api.reorderShapeStatuses
  },
  folder: {
    list: api.getFolderStatuses,
    create: api.createFolderStatus,
    update: api.updateFolderStatus,
    remove: api.deleteFolderStatus,
    reorder: api.reorderFolderStatuses
  }
}

// State for the "Manage status" modal (ManageStatusContent). Owned by ManageModalStore.
class StatusModalStore {
  @observable statusType = 'shape'
  @observable displayMode = 'fill'
  @observable items = []
  @observable loading = true
  @observable saving = false
  @observable displayModeSaving = false
  @observable error = ''
  @observable showAdd = false
  @observable newName = ''
  @observable newColour = DEFAULT_COLOUR
  @observable editingId = null
  @observable editingName = ''
  @observable picker = null
  @observable deleteItem = null
  @observable colourChange = null

  constructor () {
    makeObservable(this)
  }

  @computed get countField () {
    return this.statusType === 'shape' ? 'shape_count' : 'folder_count'
  }

  @computed get countLabel () {
    return this.statusType === 'shape' ? 'shapes' : 'folders'
  }

  @action setNewName (name) { this.newName = name }
  @action setEditingName (name) { this.editingName = name }
  @action setEditingId (id) { this.editingId = id }
  @action setPicker (picker) { this.picker = picker }
  @action setDeleteItem (item) { this.deleteItem = item }

  @action togglePicker (picker) {
    this.picker = this.picker === picker ? null : picker
  }

  @action openAdd () {
    this.showAdd = true
    this.picker = null
  }

  @action closeOverlays () {
    this.picker = null
    this.deleteItem = null
    this.colourChange = null
  }

  // Clears per-tab editing state; called whenever the modal opens or the tab changes.
  @action resetForm () {
    this.showAdd = false
    this.newName = ''
    this.newColour = DEFAULT_COLOUR
    this.picker = null
    this.editingId = null
    this.deleteItem = null
    this.colourChange = null
    this.error = ''
  }

  @action setItems (items) {
    this.items = items
    dataStore.setStatuses(this.statusType, items)
  }

  @action open () {
    this.statusType = 'shape'
    this.resetForm()
    this.loadDisplayMode()
    this.loadStatuses()
  }

  @action setStatusType (type) {
    if (type === this.statusType) return
    this.statusType = type
    this.resetForm()
    this.loadStatuses()
  }

  async loadStatuses () {
    const type = this.statusType
    runInAction(() => {
      this.loading = true
      this.error = ''
    })
    try {
      const response = await statusApi[type].list()
      const data = response.data
      const statuses = Array.isArray(data) ? data : (data && data.results) || []
      if (type !== this.statusType) return
      this.setItems(statuses)
    } catch (err) {
      if (type !== this.statusType) return
      runInAction(() => {
        this.error = fieldError(err) || 'Unable to load statuses.'
        this.items = []
      })
    } finally {
      if (type === this.statusType) runInAction(() => { this.loading = false })
    }
  }

  async loadDisplayMode () {
    try {
      // This singleton setting applies to every shape and folder status for the user.
      const response = await api.getUserConfigurations()
      const colorType = response.data.status_color_type === 'outline' ? 'outline' : 'fill'
      runInAction(() => { this.displayMode = colorType })
      dataStore.setStatusColorType(colorType)
    } catch (err) {
      runInAction(() => { this.error = fieldError(err) || 'Unable to load display colour setting.' })
    }
  }

  async changeDisplayMode (colorType) {
    if (colorType === this.displayMode || this.displayModeSaving) return
    runInAction(() => {
      this.displayModeSaving = true
      this.error = ''
    })
    try {
      // Persist first, then update the shared store used by map/sidebar rendering.
      const response = await api.updateUserConfigurations({ status_color_type: colorType })
      const savedColorType = response.data.status_color_type
      runInAction(() => { this.displayMode = savedColorType })
      dataStore.setStatusColorType(savedColorType)
    } catch (err) {
      runInAction(() => { this.error = fieldError(err, 'status_color_type') || 'Unable to update display colour setting.' })
    } finally {
      runInAction(() => { this.displayModeSaving = false })
    }
  }

  async addStatus () {
    const name = this.newName.trim()
    if (!name || this.saving) return
    runInAction(() => {
      this.saving = true
      this.error = ''
    })
    try {
      const response = await statusApi[this.statusType].create({ name, color: this.newColour })
      runInAction(() => {
        this.setItems(this.items.concat(response.data))
        this.newName = ''
        this.newColour = DEFAULT_COLOUR
        this.showAdd = false
        this.picker = null
      })
    } catch (err) {
      runInAction(() => { this.error = fieldError(err, 'name') || 'Unable to add status.' })
    } finally {
      runInAction(() => { this.saving = false })
    }
  }

  @action startEdit (item) {
    this.editingId = item.id
    this.editingName = item.name
    this.picker = null
  }

  async saveEdit () {
    const item = this.items.find(entry => entry.id === this.editingId)
    const name = this.editingName.trim()
    runInAction(() => { this.editingId = null })
    if (!item || !name || name === item.name) return
    runInAction(() => { this.error = '' })
    try {
      const response = await statusApi[this.statusType].update(item.id, { name })
      this.setItems(this.items.map(entry => entry.id === item.id ? response.data : entry))
    } catch (err) {
      runInAction(() => { this.error = fieldError(err, 'name') || 'Unable to rename status.' })
    }
  }

  @action selectColour (colour) {
    if (this.picker === 'add') {
      this.newColour = colour
      this.picker = null
      return
    }
    const item = this.items.find(entry => entry.id === this.picker)
    this.picker = null
    if (!item || item.color.toLowerCase() === colour.toLowerCase()) return
    this.colourChange = { item, nextColor: colour }
  }

  async confirmColourChange () {
    const colourChange = this.colourChange
    if (!colourChange || this.saving) return
    runInAction(() => {
      this.saving = true
      this.error = ''
    })
    try {
      const response = await statusApi[this.statusType].update(colourChange.item.id, {
        color: colourChange.nextColor
      })
      runInAction(() => {
        this.setItems(this.items.map(entry => (
          entry.id === colourChange.item.id ? response.data : entry
        )))
        this.colourChange = null
      })
    } catch (err) {
      runInAction(() => { this.error = fieldError(err, 'color') || 'Unable to update colour.' })
    } finally {
      runInAction(() => { this.saving = false })
    }
  }

  async confirmDelete () {
    const deleteItem = this.deleteItem
    if (!deleteItem || this.saving) return
    runInAction(() => {
      this.saving = true
      this.error = ''
    })
    try {
      await statusApi[this.statusType].remove(deleteItem.id)
      runInAction(() => {
        this.setItems(this.items.filter(entry => entry.id !== deleteItem.id))
        this.deleteItem = null
      })
    } catch (err) {
      runInAction(() => { this.error = fieldError(err) || 'Unable to delete status.' })
    } finally {
      runInAction(() => { this.saving = false })
    }
  }

  async reorder (next) {
    const previous = this.items
    if (next === previous || next.every((entry, index) => entry.id === previous[index].id)) return
    // Reorder optimistically and restore the previous list if the API rejects it.
    runInAction(() => { this.items = next })
    try {
      const response = await statusApi[this.statusType].reorder({ ids: next.map(entry => entry.id) })
      if (Array.isArray(response.data)) this.setItems(response.data)
    } catch (err) {
      runInAction(() => {
        this.items = previous
        this.error = fieldError(err) || 'Unable to reorder statuses.'
      })
    }
  }
}

export default StatusModalStore
